import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { FormTextEditor } from './FormEditor';
import { FormWidget } from '../../components/widgets/FormWidget';
import { createDatum, getDatum, updateDatum } from '../../api/datum';
import { parseForm } from '../../utils/formParser';
import type { Datum, DatumUser } from '../../api/datum';
import { ROUTES } from '../../utils/constants';
import { useAuthStore } from '../../stores/authStore';
import { useDatumStore } from '../../stores/datumStore';

interface EditFormProps {
  tenant: string;
}

/**
 * split page for editing form
 */
export function EditFormPage({ tenant }: EditFormProps) {
  const navigate = useNavigate();
  const { cohortId, signalId } = useParams<{ cohortId: string; signalId?: string }>();
  const isEditMode = !!signalId;
  const user = useAuthStore((state) => state.user);

  // Get kind from URL query parameter for new forms
  const urlParams = new URLSearchParams(window.location.search);
  const kindParam = urlParams.get('kind') || 'form';

  // Use datum store for library
  const {
    getDatumByPrincipal,
    loadDatumByPrincipal,
    principalLoading,
  } = useDatumStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [kind, setKind] = useState<string>(kindParam);
  const [body, setBody] = useState('');
  const [prompt, setPrompt] = useState('');
  const [schedule, setSchedule] = useState<{
    startDate: string;
    endDate?: string;
    description: string;
  }>({
    startDate: new Date().toISOString().split('T')[0],
    description: 'Once',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [parsing, setParsing] = useState(false);

  // Preview state - in-memory store for preview interactions
  const [previewDatum, setPreviewDatum] = useState<Datum | null>(null);

  // Extract title from markdown (first header)
  const extractTitle = (markdown: string): string => {
    const lines = markdown.split('\n');
    for (const line of lines) {
      const headerMatch = line.trim().match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        return headerMatch[2];
      }
    }
    return 'Untitled';
  };

  // Update preview datum when body or kind changes
  useEffect(() => {
    setPreviewDatum((prev) => ({
      id: 'preview',
      kind: kind,
      principalId: cohortId || '',
      createdBy: 'preview',
      accessMode: 'read_write',
      fixedData: {
        title: extractTitle(body),
        body: body,
      },
      datumUsers: prev?.datumUsers || [],
      createdAt: prev?.createdAt || Date.now(),
      updatedAt: Date.now(),
    }));
  }, [body, kind, cohortId]);

  // Load datum from store when component mounts or cohortId changes
  useEffect(() => {
    if (cohortId) {
      loadDatumByPrincipal(tenant, cohortId);
    }
  }, [cohortId, tenant, loadDatumByPrincipal]);

  useEffect(() => {
    if (isEditMode && signalId) {
      loadSignalForEdit();
    } else if (!isEditMode) {
      setLoading(false);
    }
  }, [signalId, tenant, isEditMode]);

  // Get forms from store and filter
  const forms = cohortId ? getDatumByPrincipal(cohortId) : [];
  const formBasedKinds = ['form', 'reminder', 'checklist', 'poll', 'goal', 'habit'];
  const allForms = forms
    .filter((s) => formBasedKinds.includes(s.kind))
    .sort((a, b) => b.createdAt - a.createdAt);

  const filteredForms = searchQuery.trim()
    ? allForms.filter((f) => {
        const query = searchQuery.toLowerCase();
        const formTitle = (f.fixedData?.title as string) || '';
        const formBody = (f.fixedData?.body as string) || '';
        return (
          formTitle.toLowerCase().includes(query) ||
          formBody.toLowerCase().includes(query)
        );
      })
    : allForms;

  const loadSignalForEdit = async () => {
    if (!signalId) return;

    setLoading(true);
    try {
      const response = await getDatum(tenant, signalId);
      const signal = response.signal;

      // Populate form fields
      setKind(signal.kind);
      setBody((signal.fixedData?.body as string) || '');
      setPrompt((signal.fixedData?.prompt as string) || '');

      // Handle schedule - support both old string format and new object format
      const scheduleData = signal.fixedData?.schedule;
      if (typeof scheduleData === 'object' && scheduleData !== null) {
        setSchedule(scheduleData as any);
      } else if (typeof scheduleData === 'string') {
        // Backward compatibility: convert old string format to object
        setSchedule({
          startDate: new Date().toISOString().split('T')[0],
          description: scheduleData,
        });
      } else {
        setSchedule({
          startDate: new Date().toISOString().split('T')[0],
          description: 'Once',
        });
      }
    } catch (error) {
      console.error('Failed to load signal:', error);
      alert('Failed to load form for editing');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!cohortId || !body.trim()) {
      alert('Please provide form content');
      return;
    }

    // Validate form syntax
    try {
      parseForm(body);
    } catch (error) {
      alert(`Form syntax error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return;
    }

    // Extract title from markdown
    const title = extractTitle(body);

    setSaving(true);
    try {
      if (isEditMode && signalId) {
        // Update existing signal
        await updateDatum(tenant, signalId, {
          fixedData: {
            title: title,
            body: body.trim(),
            schedule: schedule,
          },
        });
      } else {
        // Create new signal
        // Store the prompt if it was used to generate the form
        await createDatum(tenant, {
          kind: kind,
          principalId: cohortId,
          accessMode: 'read_write',
          fixedData: {
            title: title,
            body: body.trim(),
            schedule: schedule,
            ...(prompt.trim() && { prompt: prompt.trim() }),
          },
        });
      }

      // Refresh the library to show the new/updated form
      if (cohortId) {
        await loadDatumByPrincipal(tenant, cohortId);
      }

      // Navigate back to cohort detail
      navigate(ROUTES.coach.cohortDetail(tenant, cohortId));
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} form:`, error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} form. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (cohortId) {
      navigate(ROUTES.coach.cohortDetail(tenant, cohortId));
    }
  };

  const renderPreview = () => {
    // Mock getDatum function for preview
    const mockGetDatum = (id: string) => {
      if (id === 'preview') return previewDatum;
      return null;
    };

    // Mock updateDatumUser function for preview - updates in-memory state
    const mockUpdateDatumUser = async (tenant: string, datumId: string, data: Record<string, unknown>) => {
      if (datumId === 'preview' && previewDatum && user) {
        const now = Date.now();

        // Always add a new entry (real API creates new DatumUser on each update)
        setPreviewDatum({
          ...previewDatum,
          datumUsers: [
            ...previewDatum.datumUsers,
            {
              id: `preview-datum-user-${now}`,
              userId: user.id,
              data,
              createdAt: now,
              updatedAt: now,
            },
          ],
        });
      }
    };

    // Mock refreshDatum function for preview - triggers re-render
    const mockRefreshDatum = async () => {
      // State update already happened in mockUpdateDatumUser
      // This is just to satisfy the API contract
    };

    try {
      parseForm(body); // Validate syntax
      return (
        <FormWidget
          datumId="preview"
          tenant={tenant}
          kind={kind}
          getDatum={mockGetDatum}
          refreshDatum={mockRefreshDatum}
          updateDatumUser={mockUpdateDatumUser}
        />
      );
    } catch (error) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Parse Error:</strong> {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  };

  const loadIntoEditor = (form: Datum) => {
    setBody((form.fixedData?.body as string) || '');
    setPrompt((form.fixedData?.prompt as string) || '');

    // Handle schedule - support both old string format and new object format
    const scheduleData = form.fixedData?.schedule;
    if (typeof scheduleData === 'object' && scheduleData !== null) {
      setSchedule(scheduleData as any);
    } else if (typeof scheduleData === 'string') {
      // Backward compatibility: convert old string format to object
      setSchedule({
        startDate: new Date().toISOString().split('T')[0],
        description: scheduleData,
      });
    } else {
      setSchedule({
        startDate: new Date().toISOString().split('T')[0],
        description: 'Once',
      });
    }
  };

  const getKindDisplayName = (kind: string): string => {
    const names: Record<string, string> = {
      form: 'Form',
      reminder: 'Reminder',
      checklist: 'Checklist',
      poll: 'Poll',
      goal: 'Goal',
      habit: 'Habit',
    };
    return names[kind] || 'Form';
  };

  const handleParsePrompt = async () => {
    if (!prompt.trim()) return;

    setParsing(true);
    try {
      // @ts-ignore
      const apiBase = import.meta.env.VITE_API_URL || '';
      const url = apiBase
        ? `${apiBase}/${tenant}/api/datum/form/parse`
        : `/${tenant}/api/datum/form/parse`;

      // Get token from localStorage for Authorization header
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ input: prompt }),
      });

      if (!response.ok) {
        throw new Error('Failed to parse prompt');
      }

      const data = await response.json();
      if (data.parsed) {
        setBody(data.parsed.body || '');

        // Handle schedule from LLM response
        if (data.parsed.schedule && typeof data.parsed.schedule === 'object') {
          setSchedule(data.parsed.schedule);
        } else {
          setSchedule({
            startDate: new Date().toISOString().split('T')[0],
            description: 'Once',
          });
        }
      }
    } catch (error) {
      console.error('Failed to parse prompt:', error);
      alert('Failed to generate form from prompt. Please try again.');
    } finally {
      setParsing(false);
    }
  };

  const isLibraryLoading = cohortId ? principalLoading.get(cohortId) : false;

  if (loading) {
    return <Loading message={isEditMode ? "Loading form..." : "Loading forms..."} />;
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? `Edit ${getKindDisplayName(kind)}` : `Create ${getKindDisplayName(kind)}`}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {isEditMode ? 'Update content' : `Create ${getKindDisplayName(kind).toLowerCase()} for your cohort`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={saving}
              disabled={!body.trim()}
            >
              {isEditMode ? 'Save Changes' : `Create ${getKindDisplayName(kind)}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Main content - split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left side - Library (30%) */}
        <div className="w-[30%] border-r border-gray-200 flex flex-col bg-gray-50">
          <div className="p-4 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Library</h2>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {isLibraryLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="text-sm text-gray-500 mt-2">Loading forms...</p>
              </div>
            ) : filteredForms.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {searchQuery ? 'No forms found' : 'No previous forms'}
              </div>
            ) : (
              filteredForms.map((form) => (
                <div
                  key={form.id}
                  onClick={() => loadIntoEditor(form)}
                  className="cursor-pointer"
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm truncate">
                            {(form.fixedData?.title as string) || 'Untitled'}
                          </h3>
                          <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded mt-1">
                            {getKindDisplayName(form.kind)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {new Date(form.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 font-mono">
                        {(form.fixedData?.body as string) || ''}
                      </p>
                    </div>
                  </Card>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right side - Editor (70%) */}
        <div className="w-[70%] flex flex-col bg-white">
          <div className="p-4 border-b border-gray-200">
            {/* Prompt field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    e.preventDefault();
                    handleParsePrompt();
                  }
                }}
                placeholder="Describe what you want to track..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              <div className="flex justify-end mt-2">
                <Button
                  variant="primary"
                  onClick={handleParsePrompt}
                  isLoading={parsing}
                  disabled={!prompt.trim()}
                >
                  Generate
                </Button>
              </div>
            </div>

            {/* Schedule field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule
              </label>
              <input
                type="text"
                value={schedule.description}
                onChange={(e) => setSchedule({ ...schedule, description: e.target.value })}
                placeholder="e.g., Daily, Weekly for 4 weeks, Once"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be auto-populated when you generate from a prompt
              </p>
            </div>
          </div>

          {/* Editor/Preview content */}
          <div className="flex-1 overflow-y-auto p-4">
            {showPreview ? (
              <div className="space-y-4">
                {/* Toggle buttons in preview mode */}
                <div className="flex items-center gap-2 border-b border-gray-200 pb-4">
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowPreview(false)}
                      className="px-3 py-1 text-sm bg-white text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowPreview(true)}
                      className="px-3 py-1 text-sm border-l border-gray-300 bg-primary-600 text-white"
                    >
                      Preview
                    </button>
                  </div>
                </div>
                {/* Preview content */}
                <div>
                  {body ? (
                    renderPreview()
                  ) : (
                    <p className="text-gray-400 italic">Nothing to preview yet</p>
                  )}
                </div>
              </div>
            ) : (
              <FormTextEditor
                value={body}
                onChange={setBody}
                showPreview={showPreview}
                onTogglePreview={setShowPreview}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
